import React, { useRef } from 'react';
import { X, Printer, Download, CheckCircle2 } from 'lucide-react';
import { motion } from 'motion/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';

export interface LPOItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
}

export interface LPO {
  id: string;
  lpoNumber: string;
  supplierId: string;
  supplierName: string;
  supplierAddress?: string;
  supplierBox?: string;
  supplierTin: string;
  supplierVrn?: string;
  customerName?: string;
  jobCardNo?: string;
  requiredByDate?: string;
  currency: 'TZS' | 'USD';
  items: LPOItem[];
  subtotal: number;
  vatAmount: number;
  totalAmount: number;
  deliveryTerms: string;
  paymentTerms: string;
  preparedBy: string;
  status: 'draft' | 'pending_hod' | 'pending_accountant' | 'pending_finance' | 'pending_md' | 'approved' | 'sent' | 'cancelled';
  createdAt: any;
  hodSignature?: {
    signedBy: string;
    signedAt: any;
    signatureText: string;
  };
  accountantSignature?: {
    signedBy: string;
    signedAt: any;
    signatureText: string;
  };
  mdSignature?: {
    signedBy: string;
    signedAt: any;
    signatureText: string;
  };
}

interface LPOPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  lpo: LPO | null;
  logoUrl?: string;
}

export const LPOPrintModal: React.FC<LPOPrintModalProps> = ({ isOpen, onClose, lpo, logoUrl = '/Logo.png' }) => {
  const printRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !lpo) return null;

  const formatDate = (dateVal: any) => {
    if (!dateVal) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (typeof dateVal.toDate === 'function') {
      return dateVal.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    if (dateVal instanceof Date) {
      return dateVal.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return String(dateVal);
  };

  const handleBrowserPrint = () => {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open('', '_blank', 'width=900,height=1000');
    if (!printWindow) {
      toast.error('Please allow popups to print the LPO');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>LPO_${lpo.lpoNumber.replace(/\//g, '_')}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 12mm;
            }
            body {
              font-family: Arial, sans-serif;
              color: #000;
              margin: 0;
              padding: 0;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .lpo-container {
              width: 100%;
              max-width: 800px;
              margin: 0 auto;
            }
            .title-header {
              text-align: center;
              font-size: 22px;
              font-weight: 900;
              letter-spacing: 0.5px;
              margin-bottom: 12px;
              text-transform: uppercase;
            }
            .letterhead-box {
              display: flex;
              align-items: center;
              gap: 15px;
              margin-bottom: 12px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            .logo-img {
              width: 80px;
              height: 80px;
              object-fit: contain;
            }
            .letterhead-text {
              flex: 1;
              text-align: center;
            }
            .company-name {
              color: #d97706; /* fallback or red */
              font-size: 16px;
              font-weight: 900;
              margin: 0 0 4px 0;
            }
            .company-red {
              color: #cc0000;
              font-size: 17px;
              font-weight: 900;
            }
            .specialists {
              font-size: 11px;
              margin: 2px 0;
              color: #222;
            }
            .address-line {
              font-size: 10px;
              color: #333;
              margin: 2px 0;
            }
            .tax-line {
              color: #cc0000;
              font-size: 10px;
              font-weight: 800;
              margin-top: 4px;
            }
            /* Box Layout */
            .info-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
              border: 1.5px solid #000;
            }
            .info-table td, .info-table th {
              border: 1px solid #000;
              padding: 5px 8px;
              font-size: 11px;
            }
            .supplier-cell {
              width: 45%;
              vertical-align: top;
            }
            .lpo-no-box {
              font-size: 16px;
              font-weight: 900;
            }
            /* Items Table */
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 0;
              border: 1.5px solid #000;
            }
            .items-table th {
              border: 1px solid #000;
              padding: 6px 8px;
              font-size: 11px;
              font-weight: 800;
              text-align: left;
              background-color: #f8fafc;
            }
            .items-table td {
              border: 1px solid #000;
              padding: 6px 8px;
              font-size: 11px;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .totals-box {
              width: 100%;
              border-collapse: collapse;
              border: 1.5px solid #000;
              border-top: none;
              margin-bottom: 15px;
            }
            .totals-box td {
              border: 1px solid #000;
              padding: 6px 10px;
              font-size: 11px;
            }
            .grand-total-row {
              font-size: 13px;
              font-weight: 900;
            }
            /* Signature Box */
            .signatures-table {
              width: 100%;
              border-collapse: collapse;
              border: 1.5px solid #000;
              margin-top: 15px;
            }
            .signatures-table td {
              border: 1px solid #000;
              width: 33.33%;
              padding: 10px 12px;
              vertical-align: top;
              font-size: 11px;
              height: 90px;
            }
            .sig-title {
              font-weight: 800;
              margin-top: 15px;
              font-size: 11px;
            }
            .dotted-line {
              border-bottom: 1px dotted #000;
              display: inline-block;
              width: 120px;
              margin-left: 5px;
            }
            .sig-stamp {
              color: #1e3a8a;
              font-family: monospace;
              font-weight: bold;
              font-size: 10px;
              margin-top: 4px;
            }
          </style>
        </head>
        <body>
          <div class="lpo-container">
            ${content.innerHTML}
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    
    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text('PURCHASE ORDER', 105, 15, { align: 'center' });

    // Header letterhead box
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);

    // Red Company Name
    doc.setTextColor(204, 0, 0);
    doc.setFontSize(13);
    doc.text('BRIGHT TECHNICAL SYSTEMS & GENERAL SUPPLIES LIMITED', 105, 23, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('specialists in: Supply, Installation, Service, Repair & Maintenance of Air Conditioning Systems as well as Plumbing and Electrical Works', 105, 27, { align: 'center' });
    doc.text('P.O. Box 25002 | Tel / Fax 0222 861969 | Email: info@brighttech.co.tz', 105, 31, { align: 'center' });
    
    doc.setFont('helvetica', 'bold');
    doc.text('Ilala, Shariff Shamba, Plot No. 63, Ilala - Dar es Salaam TANZANIA', 105, 35, { align: 'center' });

    doc.setTextColor(204, 0, 0);
    doc.text('TIN: 102-960-181 VRN: 100-17403-U ISO 9001:2015 CERTIFIED | CRB REGISTERED', 105, 39, { align: 'center' });

    doc.line(10, 42, 200, 42);

    // Supplier & Order Info Box
    let y = 46;
    doc.rect(10, y, 190, 32); // Outer box
    doc.line(95, y, 95, y + 20); // Divider vertical

    // Left: Supplier
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.supplierName.toUpperCase(), 13, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.text(`P.O. BOX ${lpo.supplierBox || '2534'}`, 13, y + 11);
    doc.text(lpo.supplierAddress || 'DAR ES SALAAM.', 13, y + 16);

    // Right: Customer & LPO No.
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Name', 98, y + 6);
    doc.text('L.P.O. No.', 140, y + 6);
    doc.setFontSize(11);
    doc.text(lpo.lpoNumber.replace('LPO/BTSGS/', ''), 175, y + 6);

    doc.line(95, y + 8, 200, y + 8);

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text(lpo.customerName || 'BRIGHT HQ', 105, y + 14);
    doc.text('Date', 145, y + 14);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(lpo.createdAt), 170, y + 14);

    doc.line(10, y + 20, 200, y + 20);

    // Required date row
    doc.setFont('helvetica', 'normal');
    doc.text('Required by Date', 13, y + 26);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.requiredByDate || formatDate(lpo.createdAt), 42, y + 26);

    doc.setFont('helvetica', 'normal');
    doc.text('Job Card No.', 98, y + 26);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.jobCardNo || 'N/A', 125, y + 26);

    doc.setFont('helvetica', 'normal');
    doc.text('Terms', 160, y + 26);
    doc.setFont('helvetica', 'bold');
    doc.text(lpo.paymentTerms || 'CASH', 175, y + 26);

    // Items Table
    const tableY = y + 36;
    const headers = [['Item No.', 'Description', 'Qty', 'U/M', 'Rate', 'Amount']];
    const data = lpo.items.map((item, idx) => [
      idx + 1,
      item.description,
      item.quantity,
      item.unit || 'Pcs',
      item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })
    ]);

    autoTable(doc, {
      startY: tableY,
      head: headers,
      body: data,
      theme: 'grid',
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.3,
        lineColor: [0, 0, 0]
      },
      styles: {
        textColor: [0, 0, 0],
        fontSize: 8.5,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 85 },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 16, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 30, halign: 'right' }
      }
    });

    // Totals Box
    const finalY = (doc as any).lastAutoTable.finalY;
    
    // Subtotal row
    doc.rect(10, finalY, 190, 8);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal', 140, finalY + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${lpo.currency} ${lpo.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 195, finalY + 5.5, { align: 'right' });

    // VAT row
    doc.rect(10, finalY + 8, 190, 8);
    doc.setFont('helvetica', 'normal');
    doc.text('VAT Total', 140, finalY + 13.5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${lpo.currency} ${lpo.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 195, finalY + 13.5, { align: 'right' });

    // Total row with E. & O.E
    doc.rect(10, finalY + 16, 190, 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('E. & O.E', 13, finalY + 22.5);
    doc.text('Total', 140, finalY + 22.5);
    doc.text(`${lpo.currency} ${lpo.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 195, finalY + 22.5, { align: 'right' });

    // Signatures Block
    const sigY = finalY + 32;
    doc.rect(10, sigY, 190, 32);
    doc.line(73.3, sigY, 73.3, sigY + 32);
    doc.line(136.6, sigY, 136.6, sigY + 32);

    // Col 1: Prepared By
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Prepared By: ........................', 13, sigY + 6);
    doc.text('Signature: ........................', 13, sigY + 14);
    if (lpo.preparedBy) {
      doc.setFontSize(7);
      doc.setTextColor(30, 58, 138);
      doc.text(`[${lpo.preparedBy}]`, 13, sigY + 20);
      doc.setTextColor(0, 0, 0);
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Purchasing Officer/Store Keeper', 13, sigY + 27);

    // Col 2: Approved By (Chief Accountant)
    doc.setFont('helvetica', 'normal');
    doc.text('Aproved By: ........................', 76, sigY + 6);
    doc.text('Signature: ........................', 76, sigY + 14);
    if (lpo.accountantSignature) {
      doc.setFontSize(7);
      doc.setTextColor(30, 58, 138);
      doc.text(`[Signed: ${lpo.accountantSignature.signatureText}]`, 76, sigY + 20);
      doc.setTextColor(0, 0, 0);
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Chief Accountant', 76, sigY + 27);

    // Col 3: Authorised By (GM/MD)
    doc.setFont('helvetica', 'normal');
    doc.text('Authorised By: ........................', 139, sigY + 6);
    doc.text('Signature: ........................', 139, sigY + 14);
    if (lpo.mdSignature) {
      doc.setFontSize(7);
      doc.setTextColor(30, 58, 138);
      doc.text(`[Authorised: ${lpo.mdSignature.signatureText}]`, 139, sigY + 20);
      doc.setTextColor(0, 0, 0);
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('General Manager/MD', 139, sigY + 27);

    downloadFile(doc.output('blob'), `LPO_${lpo.lpoNumber.replace(/\//g, '_')}.pdf`).then(success => {
      if (success) toast.success('LPO PDF exported successfully!');
    });
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl w-full max-w-4xl p-6 sm:p-8 relative z-10 shadow-2xl max-h-[90vh] flex flex-col"
      >
        {/* Modal Toolbar Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-4 flex-shrink-0">
          <div>
            <span className="text-[10px] font-black text-blue-900 uppercase tracking-widest block">Official Document Printout</span>
            <h3 className="text-xl font-black text-slate-900">Local Purchase Order — {lpo.lpoNumber}</h3>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleBrowserPrint}
              className="px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs flex items-center gap-2 transition-all shadow-md active:scale-95 cursor-pointer"
            >
              <Printer className="w-4 h-4 text-blue-200" />
              <span>Print LPO</span>
            </button>
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-bold text-xs flex items-center gap-2 transition-all shadow-md active:scale-95 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-500 transition-all cursor-pointer ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Document View (Matching Image) */}
        <div className="flex-1 overflow-y-auto pr-2 bg-slate-50/50 p-4 rounded-2xl border border-slate-200">
          <div
            ref={printRef}
            className="bg-white p-8 max-w-[780px] mx-auto shadow-sm border border-slate-300 font-sans text-slate-900 leading-tight"
            style={{ minHeight: '800px' }}
          >
            {/* Title */}
            <h1 className="text-center text-2xl font-black uppercase tracking-wider mb-3 text-slate-900">
              PURCHASE ORDER
            </h1>

            {/* Letterhead Header Box */}
            <div className="flex items-center gap-4 pb-3 border-b-2 border-black mb-3">
              <img
                src={logoUrl}
                alt="Logo"
                className="w-20 h-20 object-contain flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
              <div className="flex-1 text-center space-y-1">
                <h2 className="text-red-700 text-lg font-black tracking-tight uppercase leading-snug">
                  BRIGHT TECHNICAL SYSTEMS & GENERAL SUPPLIES LIMITED
                </h2>
                <p className="text-[11px] text-slate-800 font-medium leading-tight">
                  <span className="font-bold">specialists in:</span> Supply, Installation, Service, Repair & Maintenance of Air Conditioning Systems as well as Plumbing and Electrical Works
                </p>
                <p className="text-[10px] text-slate-700">
                  P.O. Box 25002 | Tel / Fax 0222 861969 | Email: info@brighttech.co.tz
                </p>
                <p className="text-[10px] font-bold text-slate-900">
                  Ilala, Shariff Shamba, Plot No. 63, Ilala - Dar es Salaam TANZANIA
                </p>
                <p className="text-[10px] font-black text-red-700 tracking-tight">
                  TIN: 102-960-181 VRN: 100-17403-U ISO 9001:2015 CERTIFIED | CRB REGISTERED
                </p>
              </div>
            </div>

            {/* Top Details Grid Box */}
            <div className="border-2 border-black mb-0">
              <div className="grid grid-cols-12 divide-x-2 divide-black border-b-2 border-black">
                {/* Left: Supplier Info */}
                <div className="col-span-5 p-2.5 text-xs font-medium space-y-1">
                  <p className="font-black text-sm uppercase tracking-wide text-slate-900">{lpo.supplierName}</p>
                  <p>P.O BOX {lpo.supplierBox || '2534'}</p>
                  <p className="uppercase">{lpo.supplierAddress || 'DAR ES SALAAM.'}</p>
                  {lpo.supplierTin && <p className="text-[10px] font-bold text-slate-600 mt-1">TIN: {lpo.supplierTin}</p>}
                </div>

                {/* Right: Customer & LPO No */}
                <div className="col-span-7 divide-y-2 divide-black">
                  <div className="grid grid-cols-3 divide-x-2 divide-black bg-slate-50/50 font-bold text-xs text-center p-1.5">
                    <div className="col-span-1 py-1">Customer Name</div>
                    <div className="col-span-1 py-1">L.P.O. No.</div>
                    <div className="col-span-1 py-1 text-base font-black text-slate-900">{lpo.lpoNumber.replace('LPO/BTSGS/', '')}</div>
                  </div>
                  <div className="grid grid-cols-3 divide-x-2 divide-black text-xs text-center p-1.5 font-medium">
                    <div className="col-span-1 py-1 font-bold">{lpo.customerName || 'BRIGHT HQ'}</div>
                    <div className="col-span-1 py-1">Date</div>
                    <div className="col-span-1 py-1 font-bold">{formatDate(lpo.createdAt)}</div>
                  </div>
                </div>
              </div>

              {/* Bottom bar of info box */}
              <div className="grid grid-cols-3 divide-x-2 divide-black text-xs p-2 font-medium bg-white">
                <div>
                  <span className="text-slate-600 mr-2">Required by Date</span>
                  <span className="font-bold">{lpo.requiredByDate || formatDate(lpo.createdAt)}</span>
                </div>
                <div className="px-2">
                  <span className="text-slate-600 mr-2">Job Card No.</span>
                  <span className="font-bold">{lpo.jobCardNo || 'N/A'}</span>
                </div>
                <div className="px-2">
                  <span className="text-slate-600 mr-2">Terms</span>
                  <span className="font-bold uppercase">{lpo.paymentTerms || 'CASH'}</span>
                </div>
              </div>
            </div>

            {/* Main Items Table */}
            <table className="w-full border-2 border-t-0 border-black text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-black bg-slate-100 font-black text-slate-900">
                  <th className="border-r-2 border-black p-2 w-12 text-center">Item No.</th>
                  <th className="border-r-2 border-black p-2 text-left">Description</th>
                  <th className="border-r-2 border-black p-2 w-14 text-center">Qty</th>
                  <th className="border-r-2 border-black p-2 w-14 text-center">U/M</th>
                  <th className="border-r-2 border-black p-2 w-24 text-right">Rate</th>
                  <th className="p-2 w-28 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black font-medium">
                {lpo.items.map((item, idx) => (
                  <tr key={idx} className="min-h-[28px]">
                    <td className="border-r-2 border-black p-2 text-center font-bold">{idx + 1}</td>
                    <td className="border-r-2 border-black p-2 font-semibold text-slate-900">{item.description}</td>
                    <td className="border-r-2 border-black p-2 text-center">{item.quantity}</td>
                    <td className="border-r-2 border-black p-2 text-center">{item.unit || 'Pcs'}</td>
                    <td className="border-r-2 border-black p-2 text-right">{item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="p-2 text-right font-bold">{item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
                {/* Filler space if items are few */}
                {Array.from({ length: Math.max(0, 5 - lpo.items.length) }).map((_, i) => (
                  <tr key={`fill-${i}`} className="h-7">
                    <td className="border-r-2 border-black p-2">&nbsp;</td>
                    <td className="border-r-2 border-black p-2">&nbsp;</td>
                    <td className="border-r-2 border-black p-2">&nbsp;</td>
                    <td className="border-r-2 border-black p-2">&nbsp;</td>
                    <td className="border-r-2 border-black p-2">&nbsp;</td>
                    <td className="p-2">&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Subtotal & VAT Calculation Box */}
            <div className="border-2 border-t-0 border-black divide-y-2 divide-black text-xs font-semibold">
              <div className="flex justify-between items-center p-2 pl-4">
                <span className="text-slate-600 font-bold">Subtotal</span>
                <span className="font-bold font-mono">{lpo.currency} {lpo.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center p-2 pl-4">
                <span className="text-slate-600 font-bold">VAT Total</span>
                <span className="font-bold font-mono">{lpo.currency} {lpo.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center p-2.5 pl-4 bg-slate-50 text-sm font-black">
                <span className="text-base font-extrabold">E. & O.E</span>
                <div className="flex items-center gap-3">
                  <span>Total</span>
                  <span className="text-base font-black text-slate-900 font-mono">{lpo.currency} {lpo.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* Signature Box at Bottom */}
            <div className="border-2 border-t-0 border-black grid grid-cols-3 divide-x-2 divide-black text-xs p-3 min-h-[100px] mt-0">
              {/* Col 1: Prepared By */}
              <div className="flex flex-col justify-between pr-2 space-y-2">
                <div>
                  <p className="font-bold text-slate-900">Prepared By: <span className="font-normal border-b border-dotted border-black inline-block w-28"></span></p>
                  <p className="font-bold text-slate-900 mt-2">Signature: <span className="font-normal border-b border-dotted border-black inline-block w-32"></span></p>
                  {lpo.preparedBy && (
                    <p className="text-[10px] text-blue-900 font-bold mt-1">[{lpo.preparedBy}]</p>
                  )}
                </div>
                <p className="font-extrabold text-[11px] text-slate-900 pt-2">Purchasing Officer/Store Keeper</p>
              </div>

              {/* Col 2: Approved By (Chief Accountant) */}
              <div className="flex flex-col justify-between px-2 space-y-2">
                <div>
                  <p className="font-bold text-slate-900">Aproved By: <span className="font-normal border-b border-dotted border-black inline-block w-28"></span></p>
                  <p className="font-bold text-slate-900 mt-2">Signature: <span className="font-normal border-b border-dotted border-black inline-block w-32"></span></p>
                  {lpo.accountantSignature ? (
                    <p className="text-[10px] text-emerald-700 font-mono font-bold mt-1">
                      ✓ {lpo.accountantSignature.signatureText}
                    </p>
                  ) : (
                    <p className="text-[9px] text-amber-600 font-semibold mt-1 italic">[Pending Sign-off]</p>
                  )}
                </div>
                <p className="font-extrabold text-[11px] text-slate-900 pt-2">Chief Accountant</p>
              </div>

              {/* Col 3: Authorised By (General Manager/MD) */}
              <div className="flex flex-col justify-between pl-2 space-y-2">
                <div>
                  <p className="font-bold text-slate-900">Authorised By: <span className="font-normal border-b border-dotted border-black inline-block w-24"></span></p>
                  <p className="font-bold text-slate-900 mt-2">Signature: <span className="font-normal border-b border-dotted border-black inline-block w-32"></span></p>
                  {lpo.mdSignature ? (
                    <p className="text-[10px] text-emerald-700 font-mono font-bold mt-1">
                      ✓ {lpo.mdSignature.signatureText}
                    </p>
                  ) : (
                    <p className="text-[9px] text-amber-600 font-semibold mt-1 italic">[Pending Sign-off]</p>
                  )}
                </div>
                <p className="font-extrabold text-[11px] text-slate-900 pt-2">General Manager/MD</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
